import { AxiosError } from 'axios';
import type {
  GameDbCompany,
  GameDbGame,
  GamePlaySession,
  GamePlaySummary,
  PhysicalDataOpinion,
  PhysicalDataRequest,
  PhysicalDataResponse,
} from '../types';
import axiosInstance from './axiosInstance';

export interface UserGameRating {
  id: string;
  rating: 'THUMBS_DOWN' | 'THUMBS_UP' | 'DOUBLE_THUMBS_UP';
  createdAt: string;
  updatedAt: string;
}

export interface RatingSummary {
  totalRatings: number;
  thumbsDown: number;
  thumbsUp: number;
  doubleThumbsUp: number;
}

export interface CoverSearchMatch {
  id: string;
  name: string;
  providerId: string;
  firstReleaseDate: string | null;
  platforms: string[];
  matchedCover: {
    providerImageId: string;
    localizationId: string | null;
    url: string;
  };
  distance: number;
}

export interface CoverSearchResponse {
  matches: CoverSearchMatch[];
  searchInfo: {
    requestedLimit: number;
    returnedCount: number;
    model: 'small' | 'base';
    preprocessingVersion: string;
    maximumDistance: number;
  };
}

export const searchGamesByCover = async (
  getAccessToken: () => Promise<string>,
  image: File,
  limit = 10,
): Promise<CoverSearchResponse> => {
  const token = await getAccessToken();
  const formData = new FormData();
  formData.append('image', image);

  const response = await axiosInstance.post<CoverSearchResponse>('/gamedb/search-by-cover', formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { limit },
  });

  return response.data;
};

/**
 * Fetch a specific game from GameDB by ID
 * @param getToken Function to get the authentication token
 * @param id The ID of the game to fetch
 * @returns GameDbGame object
 */
export const fetchGameDbGame = async (getToken: () => Promise<string>, id: string) => {
  const token = await getToken();
  const response = await axiosInstance.get<GameDbGame>(`/gamedb/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

/**
 * Fetch a single GameDB company by providerId and providerCompanyId
 * @param getToken Function to get an auth token
 * @param providerId The provider ID (e.g., igdb)
 * @param providerCompanyId The provider company ID
 * @returns Promise with company data
 */
export const fetchGameDbCompany = async (
  getToken: () => Promise<string>,
  providerId: string,
  providerCompanyId: string,
): Promise<GameDbCompany> => {
  const token = await getToken();
  const response = await axiosInstance.get<GameDbCompany>(`/gamedb/company/${providerId}/${providerCompanyId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export const searchGameDbGames = async (
  getAccessToken: () => Promise<string>,
  searchTerm: string,
  systemId?: number,
  variantId?: number,
) => {
  const token = await getAccessToken();
  const params = new URLSearchParams();
  params.append('search', searchTerm);

  if (systemId) {
    params.append('systemId', systemId.toString());
  }
  if (variantId) {
    params.append('variantId', variantId.toString());
  }

  const response = await axiosInstance.get('/gamedb/search', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });

  return response.data;
};

/**
 * Fetch recently updated games from GameDB
 * @param getAccessToken Function to get the authentication token
 * @param providerId The provider ID (e.g., igdb)
 * @returns Promise with array of recently updated games
 */
export const fetchRecentlyUpdatedGames = async (getAccessToken: () => Promise<string>, providerId = 'igdb') => {
  const token = await getAccessToken();
  const response = await axiosInstance.get(`/gamedb/preset/${providerId}/recentUpdates`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

/**
 * Fetch recently released games from GameDB
 * @param getAccessToken Function to get the authentication token
 * @param providerId The provider ID (e.g., igdb)
 * @returns Promise with array of recently released games
 */
export const fetchRecentlyReleasedGames = async (getAccessToken: () => Promise<string>, providerId = 'igdb') => {
  const token = await getAccessToken();
  const response = await axiosInstance.get(`/gamedb/preset/${providerId}/recentReleases`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

/**
 * Fetch gameplay data for a specific game
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game to fetch gameplay data for
 * @returns Promise with gameplay summary and sessions
 */
export const fetchGamePlayData = async (getToken: () => Promise<string>, gameId: string): Promise<GamePlaySummary> => {
  const token = await getToken();

  try {
    const response = await axiosInstance.get<GamePlaySummary>(`/gamedb/${gameId}/gameplay`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      // Return default gameplay summary when no data exists
      return {
        id: '',
        userId: '',
        gameDbGameId: gameId,
        firstPlayed: null,
        lastPlayed: null,
        basicCompletion: null,
        fullCompletion: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        playSessions: [],
        steamMinutes: 0,
        earliestObservedActivity: null,
      };
    }
    throw error;
  }
};

/**
 * Create a new gameplay session
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game to create a session for
 * @param sessionData The session data to create
 * @returns Promise with the created session
 */
export const createGamePlaySession = async (
  getToken: () => Promise<string>,
  gameId: string,
  sessionData: {
    datePlayed: string;
    basicCompletion?: boolean;
    fullCompletion?: boolean;
    platformId?: string | null;
  },
): Promise<GamePlaySession> => {
  const token = await getToken();
  const response = await axiosInstance.post<GamePlaySession>(`/gamedb/${gameId}/gameplay/session`, sessionData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

/**
 * Update an existing gameplay session
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game the session belongs to
 * @param sessionId The ID of the session to update
 * @param sessionData The updated session data
 * @returns Promise with the updated session
 */
export const updateGamePlaySession = async (
  getToken: () => Promise<string>,
  gameId: string,
  sessionId: string,
  sessionData: {
    datePlayed: string;
    basicCompletion?: boolean;
    fullCompletion?: boolean;
    platformId?: string | null;
  },
): Promise<GamePlaySession> => {
  const token = await getToken();
  const response = await axiosInstance.put<GamePlaySession>(
    `/gamedb/${gameId}/gameplay/session/${sessionId}`,
    sessionData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return response.data;
};

/**
 * Delete a gameplay session
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game the session belongs to
 * @param sessionId The ID of the session to delete
 * @returns Promise with success message
 */
export const deleteGamePlaySession = async (
  getToken: () => Promise<string>,
  gameId: string,
  sessionId: string,
): Promise<{ message: string }> => {
  const token = await getToken();
  const response = await axiosInstance.delete<{ message: string }>(`/gamedb/${gameId}/gameplay/session/${sessionId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export async function fetchRecentlyPlayedGames(getToken: () => Promise<string>): Promise<any[]> {
  try {
    const token = await getToken();
    const response = await axiosInstance.get('/gamedb/preset/igdb/recentlyPlayed', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching recently played games:', error);
    return [];
  }
}

async function fetchPersonalizedPreset(
  getToken: () => Promise<string>,
  preset: 'recentlyAcquiredUnplayed' | 'collectionBacklogPicks' | 'upNext',
  providerId = 'igdb',
): Promise<any[]> {
  const token = await getToken();
  const response = await axiosInstance.get(`/gamedb/preset/${providerId}/${preset}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
}

export const fetchRecentlyAcquiredUnplayedGames = (getToken: () => Promise<string>, providerId = 'igdb') =>
  fetchPersonalizedPreset(getToken, 'recentlyAcquiredUnplayed', providerId);

export const fetchCollectionBacklogPicks = (getToken: () => Promise<string>, providerId = 'igdb') =>
  fetchPersonalizedPreset(getToken, 'collectionBacklogPicks', providerId);

export const fetchUpNextGames = (getToken: () => Promise<string>, providerId = 'igdb') =>
  fetchPersonalizedPreset(getToken, 'upNext', providerId);

/**
 * Fetch user's physical release opinion for a specific game/platform/region
 * @param getToken Function to get the authentication token
 * @param gameId The GameDB game ID
 * @param providerId The provider ID
 * @param providerPlatformId The provider platform ID
 * @param releaseCountry The release country
 * @returns Promise with user's physical release opinion
 */
export const fetchPhysicalDataOpinion = async (
  getToken: () => Promise<string>,
  gameId: string,
  providerId: string,
  providerPlatformId: string,
  releaseCountry: string,
): Promise<PhysicalDataOpinion | null> => {
  try {
    const token = await getToken();
    const response = await axiosInstance.get<PhysicalDataOpinion>(
      `/gamedb/${gameId}/physical-data/${providerId}/${providerPlatformId}/${releaseCountry}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      // Return null when no opinion exists
      return null;
    }
    throw error;
  }
};

/**
 * Save or update user's physical release opinion
 * @param getToken Function to get the authentication token
 * @param gameId The GameDB game ID
 * @param providerId The provider ID
 * @param providerPlatformId The provider platform ID
 * @param releaseCountry The release country
 * @param opinionData The opinion data to save
 * @returns Promise with the saved opinion
 */
export const savePhysicalDataOpinion = async (
  getToken: () => Promise<string>,
  gameId: string,
  providerId: string,
  providerPlatformId: string,
  releaseCountry: string,
  opinionData: PhysicalDataRequest,
): Promise<PhysicalDataResponse> => {
  const token = await getToken();
  const response = await axiosInstance.put<PhysicalDataResponse>(
    `/gamedb/${gameId}/physical-data/${providerId}/${providerPlatformId}/${releaseCountry}`,
    opinionData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return response.data;
};

/**
 * Platform types for filtering
 */
export interface GameDbPlatform {
  id: string;
  providerId: string;
  providerPlatformId: string;
  name: string;
  systemId: number | null;
  variantId: number | null;
  systemName: string | null;
  variantName: string | null;
}

export interface GameDbProvider {
  id: string;
  description: string;
  platforms: GameDbPlatform[];
}

export interface PlatformsResponse {
  providers: GameDbProvider[];
}

/**
 * Fetch all available platforms/providers for filtering
 * @param getToken Function to get the authentication token
 * @returns Promise with platforms grouped by provider
 */
export const fetchGameDbPlatforms = async (getToken: () => Promise<string>): Promise<PlatformsResponse> => {
  const token = await getToken();
  const response = await axiosInstance.get<PlatformsResponse>('/gamedb/platforms', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

/**
 * Search for multiple games by name with optional system or provider filtering
 * @param getToken Function to get the authentication token
 * @param gameNames Array of game names to search for (max 100)
 * @param systemId Optional system ID for filtering results
 * @param variantId Optional variant ID for filtering results
 * @param providerId Optional provider ID for filtering results (alternative to systemId/variantId)
 * @param providerPlatformId Optional provider platform ID for filtering results (used with providerId)
 * @returns Promise with bulk search results
 */
export interface BulkSearchMatch {
  id: string;
  name: string;
  confidence: 'high' | 'medium' | 'low';
  coverUrl: string | null;
  firstReleaseDate: string | null;
  platforms?: string[];
}

export interface BulkSearchResult {
  inputName: string;
  matches: BulkSearchMatch[];
  status: 'matched' | 'no_match' | 'multiple_matches';
}

export interface BulkSearchResponse {
  results: BulkSearchResult[];
}

export const bulkSearchGames = async (
  getToken: () => Promise<string>,
  gameNames: string[],
  systemId?: number,
  variantId?: number,
  providerId?: string,
  providerPlatformId?: string,
): Promise<BulkSearchResponse> => {
  const token = await getToken();

  // Validate input
  if (!Array.isArray(gameNames) || gameNames.length === 0 || gameNames.length > 100) {
    throw new Error('gameNames must be an array with 1-100 items');
  }

  // Validate that both systemId/variantId and providerId/providerPlatformId are not used together
  const hasSystemFilter = systemId || variantId;
  const hasProviderFilter = providerId || providerPlatformId;

  if (hasSystemFilter && hasProviderFilter) {
    throw new Error(
      'Cannot use systemId/variantId and providerId/providerPlatformId together. Choose one filtering method.',
    );
  }

  // Validate that if providerId is provided, providerPlatformId is also provided
  if (providerId && !providerPlatformId) {
    throw new Error('providerPlatformId is required when providerId is provided');
  }

  if (providerPlatformId && !providerId) {
    throw new Error('providerId is required when providerPlatformId is provided');
  }

  const requestData = {
    gameNames,
    ...(systemId && { systemId }),
    ...(variantId && { variantId }),
    ...(providerId && { providerId }),
    ...(providerPlatformId && { providerPlatformId }),
  };

  const response = await axiosInstance.post<BulkSearchResponse>('/gamedb/bulk-search', requestData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  return response.data;
};

/**
 * Similar games types and interfaces
 */
export interface SimilarGame {
  id: string;
  name: string;
  similarity_score: number;
  firstReleaseDate: string | null;
  coverImage: { url: string } | null;
}

export interface SimilarGamesResponse {
  similar_games: SimilarGame[];
  search_info: {
    query_game_id: string;
    query_game_name: string;
    results_count: number;
    filters_applied: Record<string, any>;
    similarity_scores?: Array<{
      game_id: string;
      game_name: string;
      similarity_score: number;
    }>;
  };
}

/**
 * Fetch similar games based on AI semantic similarity
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game to find similar games for
 * @param options Optional filtering and configuration options
 * @returns Promise with similar games and search metadata
 */
export const fetchSimilarGames = async (
  getToken: () => Promise<string>,
  gameId: string,
  options?: {
    limit?: number;
    platformId?: string;
    releaseYear?: string;
    inCollectionStatus?: 'owned' | 'unplayed' | 'completed';
  },
): Promise<SimilarGamesResponse> => {
  const token = await getToken();

  const params = new URLSearchParams();
  if (options?.limit) {
    params.append('limit', options.limit.toString());
  }
  if (options?.platformId) {
    params.append('platform_id', options.platformId);
  }
  if (options?.releaseYear) {
    params.append('release_year', options.releaseYear);
  }
  if (options?.inCollectionStatus) {
    params.append('in_collection_status', options.inCollectionStatus);
  }

  const url = `/gamedb/${gameId}/similar${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await axiosInstance.get<SimilarGamesResponse>(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

/**
 * Get user's rating for a specific game
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game
 * @returns UserGameRating object or null if no rating exists
 */
export const getUserGameRating = async (
  getToken: () => Promise<string>,
  gameId: string,
): Promise<UserGameRating | null> => {
  try {
    const token = await getToken();
    const response = await axiosInstance.get<UserGameRating>(`/gamedb/games/${gameId}/rating`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return null; // No rating found
    }
    throw error;
  }
};

/**
 * Create or update user's rating for a game
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game
 * @param rating The rating value
 * @returns Updated UserGameRating object
 */
export const setUserGameRating = async (
  getToken: () => Promise<string>,
  gameId: string,
  rating: UserGameRating['rating'],
): Promise<UserGameRating> => {
  const token = await getToken();
  const response = await axiosInstance.post<UserGameRating>(
    `/gamedb/games/${gameId}/rating`,
    { rating },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
};

/**
 * Delete user's rating for a game
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game
 */
export const deleteUserGameRating = async (getToken: () => Promise<string>, gameId: string): Promise<void> => {
  const token = await getToken();
  await axiosInstance.delete(`/gamedb/games/${gameId}/rating`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get rating summary/statistics for a game
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game
 * @returns RatingSummary object with rating counts
 */
export const getGameRatingSummary = async (getToken: () => Promise<string>, gameId: string): Promise<RatingSummary> => {
  const token = await getToken();
  const response = await axiosInstance.get<RatingSummary>(`/gamedb/games/${gameId}/ratings/summary`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export type AdminQueueTask =
  | 'igdb-sync'
  | 'classification'
  | 'steam-sync'
  | 'steam-store-sync'
  | 'wikipedia-sync'
  | 'wikipedia-extraction'
  | 'screenshot-summary';

export interface QueueAdminTaskResponse {
  success: boolean;
  message: string;
  jobId: string;
  gameId: string;
}

/**
 * Queue an admin task for a specific game
 */
export const queueAdminTask = async (
  getToken: () => Promise<string>,
  gameId: string,
  task: AdminQueueTask,
  options?: Record<string, boolean>,
): Promise<QueueAdminTaskResponse> => {
  const token = await getToken();
  const response = await axiosInstance.post<QueueAdminTaskResponse>(
    `/gamedb/games/${gameId}/queue`,
    { task, options },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  return response.data;
};

export interface ClassificationTier {
  tier: number;
  name: string;
  confidence: string;
  rationale: string | null;
}

export interface GameClassification {
  tier: string;
  reach: ClassificationTier;
  acclaim: ClassificationTier;
  legacy: ClassificationTier;
  notoriety: ClassificationTier;
  lastClassified: string | null;
  status: string;
}

/**
 * Fetch classification data for a specific game
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game
 * @returns GameClassification object
 */
export const fetchGameClassification = async (
  getToken: () => Promise<string>,
  gameId: string,
): Promise<GameClassification> => {
  const token = await getToken();
  const response = await axiosInstance.get<GameClassification>(`/gamedb/games/${gameId}/classification`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

// Types for the reviews endpoint
export interface IgdbRatings {
  userRating: number | null;
  userRatings: number | null;
  criticRating: number | null;
  criticRatings: number | null;
}

export interface SteamRatingData {
  reviewScore: number;
  reviewScoreDesc: string;
  totalPositive: number;
  totalNegative: number;
  lastSync: string;
}

export interface PublicationReview {
  id: string;
  publication: string;
  platformReviewed: string | null;
  edition: string | null;
  rawScore: string;
  normalizedScore: number | null;
  reviewDate: string | null;
  reviewUrl: string | null;
  confidence: string;
}

export interface GameReviewsResponse {
  igdb: IgdbRatings | null;
  steam: SteamRatingData | null;
  publicationReviews: PublicationReview[];
}

/**
 * Fetch comprehensive review data for a game
 * @param getToken Function to get the authentication token
 * @param gameId The ID of the game
 * @returns GameReviewsResponse with IGDB, Steam, and publication reviews
 */
export type MissingWikipediaSortBy = 'steamReviews' | 'igdbUserRatings' | 'igdbCriticRatings';

export interface MissingWikipediaGame {
  id: string;
  name: string;
  gameId: string;
  url: string | null;
  userRating: number | null;
  userRatings: number | null;
  criticRating: number | null;
  criticRatings: number | null;
  steamTotalReviews: number | null;
  steamReviewScore: number | null;
  steamReviewScoreDesc: string | null;
}

export interface MissingWikipediaResponse {
  games: MissingWikipediaGame[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const fetchMissingWikipediaGames = async (
  getToken: () => Promise<string>,
  params: { sortBy?: MissingWikipediaSortBy; page?: number; limit?: number; hideDerivative?: boolean },
): Promise<MissingWikipediaResponse> => {
  const token = await getToken();
  const searchParams = new URLSearchParams();
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.hideDerivative) searchParams.set('hideDerivative', 'true');

  const response = await axiosInstance.get<MissingWikipediaResponse>(
    `/gamedb/admin/missing-wikipedia?${searchParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return response.data;
};

export const fetchGameReviews = async (
  getToken: () => Promise<string>,
  gameId: string,
): Promise<GameReviewsResponse> => {
  const token = await getToken();
  const response = await axiosInstance.get<GameReviewsResponse>(`/gamedb/games/${gameId}/reviews`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};
